import store from '@/store'
import Echo from "laravel-echo";
import Vue from 'vue'; // Импортируем сам Vue
import axios from 'axios'
import { getRooms } from "@/components/Message/js/Rooms.js";
import { syncRoomPreviewFromChat } from "@/components/Message/js/Messages.js";
import {
    getFirebaseMessagingServiceWorkerRegistration,
    isPushFeatureAvailable
} from "@/js/push-support.js";

let reconnectTimer = null;
let resumeSyncTimer = null;
let resumeSyncPromise = null;
const typingTimers = new Map();
let presenceChannel = null;

function typingTimerKey(roomId, userId) {
    return `${roomId}:${userId}`;
}

function getTypingUsersForRoom(roomId) {
    const key = String(roomId || '');
    return Array.isArray(store.state.TypingUsersByRoom?.[key])
        ? [...store.state.TypingUsersByRoom[key]]
        : [];
}

function setTypingUsersForRoom(roomId, users) {
    store.commit('SetTypingUsersForRoom', {
        roomId,
        users,
    });
}

function upsertTypingUser(roomId, user) {
    const users = getTypingUsersForRoom(roomId);
    const nextUsers = users.filter((item) => String(item.id) !== String(user.id));

    nextUsers.push({
        id: user.id,
        name: user.name,
    });

    setTypingUsersForRoom(roomId, nextUsers);
}

function removeTypingUser(roomId, userId) {
    const users = getTypingUsersForRoom(roomId);
    const nextUsers = users.filter((item) => String(item.id) !== String(userId));

    if (!nextUsers.length) {
        store.commit('ClearTypingUsersForRoom', roomId);
        return;
    }

    setTypingUsersForRoom(roomId, nextUsers);
}

function armTypingTimeout(roomId, userId) {
    const key = typingTimerKey(roomId, userId);
    const currentTimer = typingTimers.get(key);

    if (currentTimer) {
        clearTimeout(currentTimer);
    }

    typingTimers.set(key, setTimeout(() => {
        typingTimers.delete(key);
        removeTypingUser(roomId, userId);
    }, 1800));
}

function applyDeliveredReadStatus(payload) {
    if (!payload) {
        return;
    }

    if (payload.senderId == store.state.UserData.user.id) {
        let ind = store.state.ChatWinData.findIndex(el => el._id == payload.messageId);

        if (ind !== -1) {
            const message = store.state.ChatWinData[ind];

            let read = [...(message.read_user_ids || [])];
            let unread = [...(message.unread_user_ids || [])];

            unread = unread.filter(id => id != payload.returned_user_id);

            if (!read.includes(payload.returned_user_id)) {
                read.push(payload.returned_user_id);
            }

            const status = unread.length === 0 ? 'read' : 'delivered';

            const updatedMessage = {
                ...message,
                read_user_ids: read,
                unread_user_ids: unread,
                status: status
            };

            Vue.set(store.state.ChatWinData, ind, updatedMessage);
        }

        closeRoomNotifications(payload.roomId);
    }

    if (payload.returned_user_id == store.state.UserData.user.id) {
        closeRoomNotifications(payload.roomId);
    }
}

function applyTypingPayload(payload) {
    const roomId = String(payload?.roomId || '');
    const userId = String(payload?.senderId || '');
    const currentUserId = String(store.state.UserData?.user?.id || '');

    if (!roomId || !userId || userId === currentUserId) {
        return;
    }

    if (!payload.status) {
        const key = typingTimerKey(roomId, userId);
        const timer = typingTimers.get(key);

        if (timer) {
            clearTimeout(timer);
            typingTimers.delete(key);
        }

        removeTypingUser(roomId, userId);
        return;
    }

    upsertTypingUser(roomId, {
        id: userId,
        name: payload.username || payload.name || 'Кто-то',
    });
    armTypingTimeout(roomId, userId);
}

function markSenderAsReadForOwnMessages(roomId, readerId) {
    const myId = store.state.UserData?.user?.id;

    store.state.ChatWinData.forEach((message, index) => {
        if (message.senderId != myId) {
            return;
        }

        let read = [...(message.read_user_ids || [])];
        let unread = [...(message.unread_user_ids || [])];

        if (!unread.includes(readerId)) {
            return;
        }

        unread = unread.filter(id => id != readerId);

        if (!read.includes(readerId)) {
            read.push(readerId);
        }

        const status = unread.length === 0 ? 'read' : 'delivered';

        Vue.set(store.state.ChatWinData, index, {
            ...message,
            read_user_ids: read,
            unread_user_ids: unread,
            status,
        });
    });
}

export function setLocalTypingState(roomId, userId, username, isTyping) {
    applyTypingPayload({
        roomId,
        senderId: userId,
        username,
        status: isTyping,
    });
}

export async function persistReadStatus(roomId, messageId = null) {
    if (!roomId || !store.state?.UserData?.access_token) {
        return null;
    }

    return axios({
        method: 'post',
        url: store.state.urlPathAuth + '/message/read',
        data: {
            roomid: roomId,
            mid: messageId,
        },
        headers: {
            Authorization: 'Bearer ' + store.state.UserData.access_token,
        },
    }).catch((error) => {
        console.warn('Не удалось сохранить статус прочтения', error);
        return null;
    });
}

function getCurrentRouteRoomId() {
    if (typeof window === "undefined" || !window.location) {
        return null;
    }

    const match = window.location.pathname.match(/^\/room\/(\d+)$/);
    return match ? match[1] : null;
}

async function closeRoomNotifications(roomId) {
    if (!roomId || typeof navigator === "undefined" || !("serviceWorker" in navigator) || !isPushFeatureAvailable()) {
        return;
    }

    try {
        const registration = await getFirebaseMessagingServiceWorkerRegistration();
        const worker = registration?.active || registration?.waiting || registration?.installing;

        if (!worker) {
            return;
        }

        worker.postMessage({
            type: "CLOSE_ROOM_NOTIFICATIONS",
            roomId: roomId,
        });
    } catch (error) {
        console.warn("Не удалось закрыть уведомления комнаты на клиенте:", error);
    }
}

function syncStateAfterResume() {
    if (resumeSyncPromise) {
        return resumeSyncPromise;
    }

    if (resumeSyncTimer) {
        clearTimeout(resumeSyncTimer);
    }

    resumeSyncPromise = new Promise((resolve) => {
        resumeSyncTimer = setTimeout(() => {
            resumeSyncTimer = null;

            const activeRoomId = getCurrentRouteRoomId() || (
                store.state.RoomOpen && store.state.RoomOpen.roomId
                    ? store.state.RoomOpen.roomId
                    : null
            );

            const syncRequest = activeRoomId
                ? getRooms(activeRoomId, { silentLoading: true, autoScroll: false })
                : getRooms(null, { silentLoading: true, autoScroll: false });

            Promise.resolve(syncRequest)
                .catch((error) => {
                    console.error("Не удалось синхронизировать комнаты после возврата в приложение", error);
                })
                .finally(() => {
                    resumeSyncPromise = null;
                    resolve();
                });
        }, 250);
    });

    return resumeSyncPromise;
}

function getPresenceChannel() {
    if (presenceChannel) {
        return presenceChannel;
    }

    if (!window.Echo) {
        return null;
    }

    presenceChannel = window.Echo.join(store.state.chatChannelWebsocket);

    return presenceChannel;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function webSocketConnect(options = {}) {

    const { force = false } = options;

    if (window.Echo?.connector?.pusher && !force) {
        const state = window.Echo.connector.pusher.connection?.state;

        if (state === "connected" || state === "connecting") {
            return window.Echo;
        }
    }

    if (window.Echo?.connector?.pusher && force) {
        window.Echo.disconnect();
    }


    /// подключаем вебсокет

    window.Pusher = require("pusher-js");

    let token = JSON.parse(localStorage.getItem("UserData")).access_token;

    window.Echo = new Echo({
        broadcaster: "pusher",
        key: "123456",
        cluster: "123456",
        wsHost:
            window.location.hostname === "localhost1"
                ? window.location.hostname
                : "service.metizi.net",
        //wsHost: window.location.hostname,
        //wsHost: 'service.metizi.net',
        //wsHost: (window.location.hostname === 'localhost') ? '127.0.0.1' : 'service.metizi.net',
        authEndpoint:
            window.location.hostname === "localhost"
                ? window.location.protocol + "//localhost:8001/broadcasting/auth"
                : window.location.protocol +
                "//service.metizi.net/api/websocket/broadcasting/auth",
        //authEndpoint: window.location.protocol+'//service.metizi.net/api/websocket/broadcasting/auth',
        encrypted: true,
        //wsPort: 6001,
        //wssPort: 6001,
        wsPort: window.location.hostname === "localhost" ? "6001" : "80",
        wssPort: window.location.hostname === "localhost" ? "6001" : "443",
        //wsPath: '/ws',
        disableStats: true,
        forceTLS: false,
        // wsPort: 6001,
        // wssPort: 6001,
        // disableStats: true,
        enabledTransports: ["ws", "wss"],
        auth: {
            headers: {
                authorization: "Bearer " + token,
            },
        },
    });

    window.Echo.connector.pusher.connection.bind("connecting", (payload) => {
        //this.WebSocketState = {'color': 'yellow', 'state': 'connecting...'}
        store.commit("UpdateWebsocketStatus", false);
        //this.online = 0
        console.log("connecting...", payload);
    });

    window.Echo.connector.pusher.connection.bind("connected", (payload) => {
        //this.WebSocketState = {'color': 'white', 'state': 'connected!'}
        store.commit("UpdateWebsocketStatus", true);
        //this.online =1
        console.log("connected!", payload);

        syncStateAfterResume();
    });

    window.Echo.connector.pusher.connection.bind("unavailable", (payload) => {
        //this.WebSocketState = {'color': 'red', 'state': 'unavailable'}
        store.commit("UpdateWebsocketStatus", false);
        //this.online = 0
        console.log("unavailable", payload);
    });

    window.Echo.connector.pusher.connection.bind("failed", (payload) => {
        //t/his.WebSocketState = {'color': 'red', 'state': 'failed'}
        store.commit("UpdateWebsocketStatus", false);
        //this.online = 0
        console.log("failed", payload);
    });

    window.Echo.connector.pusher.connection.bind("disconnected", (payload) => {
        //this.WebSocketState = {'color': 'red', 'state': 'disconnected'}
        store.commit("UpdateWebsocketStatus", false);
        //this.online = 0
        console.log("disconnected", payload);
    });

    //// это кто сейчас на сайте

    presenceChannel = window.Echo.join(store.state.chatChannelWebsocket);

    presenceChannel
        .here((users) => {
            store.commit("UpdateUserOnline", users);
        })
        /// пользователь зашел в чат
        .joining((user) => {

            store.commit('SetUserOnlineState', {
                user,
                isOnline: true
            })

        })
        /// пользователь вышел из чата
        .leaving((user) => {
            console.log(user.login + " " + user.id + " offline");

            store.commit('SetUserOnlineState', {
                user,
                isOnline: false
            })

        })
        .error((error) => {
            //this.ConsoleWebSocket.unshift({'icon':'mdi-account','color':'red','message': error })
            console.error(error);
        })
        // /// получаем сообщения
        .listen("ChannelPresence", (e) => {
            const currentUserId = String(store.state.UserData?.user?.id ?? '');
            const senderId = String(e.data?.senderId ?? '');
            const isOwnMessage = senderId !== '' && senderId === currentUserId;

            /// новое сообщение
            /// обычное сообщение
            if (e.data?.type == 'mes' && e.data?.roomId == store.state.RoomOpen?.roomId) {

                /// это чтобы себе не приходило
                if (!isOwnMessage) {
                    markSenderAsReadForOwnMessages(e.data?.roomId, e.data.senderId);
                    store.state.ChatWinData.push(e.data);
                    persistReadStatus(e.data?.roomId, e.data?._id);

                    if (Vue.prototype.$eventBus) {
                        Vue.prototype.$eventBus.$emit("scrollToBottom",0, true);
                    }
                    /// отправляем статус о прочтении
                    sendStatusMessage("statusmes", e.data?.roomId, e.data.senderId, e.data._id, "read", store.state.UserData.user.id)
                }
            }
            /// ставим статус (циферку) возле пользователя
            if (e.data?.type == 'mes' && !isOwnMessage) {  /// && e.data?.roomId != store.state.RoomOpen?.roomId


                const ind = store.state.Rooms.findIndex(el => el.roomId === e.data?.roomId);

                if (ind !== -1) {
                    // 1. Делаем копию комнаты
                    let roomCopy = { ...store.state.Rooms[ind] };

                    // 2. Подготавливаем данные для lastMessage (только текст и имя)
                    let lastMsgData = {
                        fio: e.data.username,
                        content: e.data.content
                    };

                    // 3. Считаем новые непрочитанные
                    //let isChattingNow = store.state.RoomOpen?.roomId === e.data.roomId;
                    // Берем текущее значение из корня (или 0) и прибавляем 1
                    //let currentUnread = parseInt(roomCopy.unreadCount) || 0;
                    //let newUnreadCount = isChattingNow ? 0 : currentUnread + 1;
                    let newUnreadCount = roomCopy.unreadCount + 1

                    // 4. Обновляем свойства на одном уровне
                    roomCopy.lastMessage = JSON.stringify(lastMsgData); // Строка с контентом
                    roomCopy.unreadCount = newUnreadCount;              // Число рядом
                    roomCopy.lastMessageAt  = new Date();

                    // 5. Сохраняем в Store через Vue.set
                    Vue.set(store.state.Rooms, ind, roomCopy);

                    // Теперь это выведет актуальное число
                    console.log('Текущий счетчик в корне:', store.state.Rooms[ind].unreadCount);
                }

            }

            /// обновить коматы если поменяли или создали
            if (e.data?.type == 'roomUpdate') {
                const activeRoomId = getCurrentRouteRoomId() || (
                    store.state.RoomOpen && store.state.RoomOpen.roomId
                        ? store.state.RoomOpen.roomId
                        : null
                );

                /// список гр
                getRooms(activeRoomId || null)
                /// удаляем данные чата
                if (store.state.RoomOpen?.roomId == e.data.roomId) {
                    store.commit('UpdateChatWinData', [])
                }

            }

            if (e.data?.type == 'messageReaction') {
                applyLocalMessageReactions(e.data.messageId, e.data.reactions);
            }

            if (e.data?.type == 'statusmes') {
                applyDeliveredReadStatus(e.data);
            }

            if (e.data?.type == 'typing') {
                applyTypingPayload(e.data);
            }


            // console.log(e.data)
        })

        /////// ставим статус что новое сообщение прочитано
        .listenForWhisper('statuschat', (e) => {

            /// это если нам пришел Шепот
            //// ставим статус прочитано если чат открыт
            if (e.type == 'statusmes' && e.senderId == store.state.UserData.user.id) {
                applyDeliveredReadStatus(e);
            }



            //// если ваш собеседник зашел в чат ставим отмечаем все сообщения прочитанные
            if (e.type == 'openchat' && e.roomId == store.state.RoomOpen?.roomId) {

                const readerId = e.returned_user_id;
                const myId = store.state.UserData?.user?.id;

                store.state.ChatWinData.forEach((message, index) => {
                    // 👉 обновляем только МОИ сообщения
                    if (message.senderId != myId) return;

                    let read = [...(message.read_user_ids || [])];
                    let unread = [...(message.unread_user_ids || [])];

                    // если уже не в unread — пропускаем
                    if (!unread.includes(readerId)) return;

                    // убираем из unread
                    unread = unread.filter(id => id != readerId);

                    // добавляем в read
                    if (!read.includes(readerId)) {
                        read.push(readerId);
                    }

                    const status = unread.length === 0 ? 'read' : 'delivered';

                    Vue.set(store.state.ChatWinData, index, {
                        ...message,
                        read_user_ids: read,
                        unread_user_ids: unread,
                        status
                    });
                });

                closeRoomNotifications(e.roomId);


                // store.state.ChatWinData.forEach((msg, index) => {
                //     if (msg.senderId == e.senderId && msg.status !== e.status) {
                //         // Создаем обновленную копию объекта
                //         const updatedMessage = {
                //             ...msg,
                //             status: e.status
                //         };

                //         // Используем Vue.set, чтобы интерфейс мгновенно перерисовался
                //         Vue.set(store.state.ChatWinData, index, updatedMessage);
                //     }
                // });
            }

            if (e.type == 'openchat' && e.roomId != store.state.RoomOpen?.roomId) {
                closeRoomNotifications(e.roomId);
            }

            //// если у всех сообщение удалено удаляем
            if (e.type == 'deleteMessage') {
                if (e.roomId == store.state.RoomOpen?.roomId) {
                    store.commit("REMOVE_MESSAGE", e.messageId);
                    syncRoomPreviewFromChat(e.roomId);
                }

                closeRoomNotifications(e.roomId);

                const activeRoomId = getCurrentRouteRoomId() || (
                    store.state.RoomOpen && store.state.RoomOpen.roomId
                        ? store.state.RoomOpen.roomId
                        : null
                );

                getRooms(activeRoomId || null, { silentLoading: true, autoScroll: false });
            }

            if (e.type == 'editMessage') {
                applyLocalMessageEdit(e.messageId, e.roomId, e.status);
            }

            if (e.type == 'typing') {
                applyTypingPayload(e);
            }

        })





}

export function disconnectWebSocket() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    if (window.Echo?.connector?.pusher) {
        window.Echo.disconnect();
    }

    store.commit("UpdateWebsocketStatus", false);
}

export function reconnectWebSocket() {
    if (localStorage.getItem("UserData") == null) {
        return;
    }

    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
    }

    disconnectWebSocket();

    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        webSocketConnect({ force: true });
    }, 500);
}

export function resyncChatStateAfterResume() {
    if (localStorage.getItem("UserData") == null) {
        return Promise.resolve();
    }

    return syncStateAfterResume();
}

export function clearRoomUnreadCount(roomId) {
    const ind = store.state.Rooms.findIndex(el => String(el.roomId) === String(roomId));

    if (ind === -1) {
        return false;
    }

    Vue.set(store.state.Rooms, ind, {
        ...store.state.Rooms[ind],
        unreadCount: 0
    });

    return true;
}

export function applyLocalMessageEdit(messageId, roomId, encodedContent) {
    const messageIndex = store.state.ChatWinData.findIndex((m) => m._id == messageId);

    if (messageIndex !== -1) {
        const message = store.state.ChatWinData[messageIndex];

        Vue.set(store.state.ChatWinData, messageIndex, {
            ...message,
            content: encodedContent,
            edited: true,
            updatedAt: Date.now(),
        });
    }

    const roomIndex = store.state.Rooms.findIndex((room) => String(room.roomId) === String(roomId));
    if (roomIndex === -1) {
        return;
    }

    const isLastMessageInOpenRoom =
        String(store.state.RoomOpen && store.state.RoomOpen.roomId) === String(roomId) &&
        store.state.ChatWinData.length > 0 &&
        String(store.state.ChatWinData[store.state.ChatWinData.length - 1]._id) === String(messageId);

    if (!isLastMessageInOpenRoom) {
        return;
    }

    const roomCopy = Object.assign({}, store.state.Rooms[roomIndex]);

    let lastMsgData = {};
    try {
        lastMsgData = roomCopy.lastMessage ? JSON.parse(roomCopy.lastMessage) : {};
    } catch (error) {
        lastMsgData = {};
    }

    roomCopy.lastMessage = JSON.stringify({
        ...lastMsgData,
        content: encodedContent,
    });

    Vue.set(store.state.Rooms, roomIndex, roomCopy);
}

export function applyLocalMessageReactions(messageId, reactions) {
    const messageIndex = store.state.ChatWinData.findIndex((m) => m._id == messageId);

    if (messageIndex === -1) {
        return;
    }

    const message = store.state.ChatWinData[messageIndex];

    Vue.set(store.state.ChatWinData, messageIndex, {
        ...message,
        reactions: Array.isArray(reactions) ? reactions : [],
    });
}

///Это механизм Whisper (шепот) в Laravel Echo. Его главная особенность — это передача событий напрямую от клиента к клиенту, минуя базу данных и контроллеры Laravel.

export function sendStatusMessage(type, roomId, senderId, messageId, status, returned_user_id = null) {
    const channel = getPresenceChannel();

    if (!channel) {
        return;
    }

    channel.whisper('statuschat', {
            status: status,
            roomId: roomId,
            senderId: senderId,
            messageId: messageId,
            returned_user_id: returned_user_id,
            type: type
        });
}

export function sendTypingStatus(roomId, isTyping, username = null) {
    if (!roomId || !window.Echo) {
        return;
    }

    const channel = getPresenceChannel();

    if (!channel) {
        return;
    }

    channel.whisper('statuschat', {
            type: 'typing',
            roomId: roomId,
            senderId: store.state.UserData?.user?.id,
            username: username || store.state.UserData?.user?.fio || 'Кто-то',
            status: !!isTyping,
        });
}
